import { useNavigate, useParams } from "react-router-dom";
import {
  ANNOUNCEMENT_LIST_ROUTE,
  ANNOUNCEMENT_UPDATE_ROUTE,
} from "constants/routeEnum";
import callApi from "Common/utils/apiCaller";
import HtmlCode from "Common/Components/HtmlCode/HtmlCode";
import announcementType from "../../constants/announcementType.json";
import * as Yup from "yup";
import { useFormik } from "formik";
import { X } from "lucide-react";
import { showNotification } from "Common/utils/Notification";
import { useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { imageUploader } from "Common/utils/imageUploader";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import { getImageUrl } from "Common/utils/ImageDomain";
import PermissionSwitch from "../../Common/Components/PermissionLocks/PermissionSwitch";
import { ToastContainer } from "react-toastify";
import ReactSelect from "Common/Components/ReactSelect/ReactSelect";
import BreadCrumb from "Common/BreadCrumb";
import Drawer from "Common/Components/Drawer";
import ImageView from "Common/Components/ImageView/ImageView";
import LoaderComponent from "Common/Components/Loader/Loader";
import CustomInput from "Common/Components/InputFields/CustomInput";
import MetaData from "Common/MetaData";
import moment from "moment";
import CustomDateInput from "Common/Components/InputFields/CustomDateInput";
import {
  apiMessage,
  currentEditStep,
  documentLabel,
  documentUploadedAt,
  keepHeading,
  sameDocument,
} from "./announcementEditHelpers";

const UpdateAnnouncement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState<any>({ released_at: "" });
  const [isLoading, setIsLoading] = useState<any>(false);
  const [version, setVersion] = useState<any>();
  const [imageLink, setImageLink] = useState<any>();
  const [content, setContent] = useState<any>();
  const [contentType, setContentType] = useState<any>();
  const [documentType, setDocumentType] = useState("");
  const [announcementData, setAnnouncementData] = useState<any>();
  const [loader, setLoader] = useState<any>(false);
  const [imageData, setImageData] = useState<any>("");
  const [showMeta, setShowMeta] = useState<any>(false);
  const toggleMeta = () => setShowMeta(!showMeta);
  const [showImage, setShowImage] = useState<any>(false);
  const toggleImage = () => setShowImage(!showImage);
  const [showApprove, setShowApprove] = useState(false);
  const [metaEdit, setMetaEdit] = useState<any>({
    url: "",
    page_title: "",
    meta_description: "",
    meta_keyword: "",
    canonical_url: "",
  });

  const [heading, setHeading] = useState<any>("");
  const [headingError, setHeadingError] = useState<any>(false);

  const [errors, setErrors] = useState<any>();
  const [announcementUrl, setAnnouncementUrl] = useState<string | null>(null);

  useEffect(() => {
    if (imageData && imageData?.media_url) {
      if (imageData?.media_type === "document") {
        let fileName = imageData?.file_name?.split(".");
        fileName.pop();
        fileName = fileName.join(" ");
        // Only fill the heading from the file name when there is none; never rename an existing one.
        setHeading((current) => keepHeading(current, fileName));
        setImageLink(imageData);
      } else {
        showNotification("Please select pdf", "error");
      }
    }
  }, [imageData]);

  const selectProperties = createSelector(
    (state: any) => state.Login,
    (profile) => ({
      permission: profile.permission,
      user: profile.user,
    })
  );

  const { permission, user } = useSelector(selectProperties);

  async function fetchannouncement(id: any) {
    try {
      const res = await callApi(`v1/announcement/${id}`, "get", {});
      if (res && res?.status === "Success") {
        setImageLink(res.data.announcement);
        setAnnouncementData(res.data.announcement);
        setFormData({
          released_at: res.data.announcement?.released_at,
        });
        setContent(res.data.announcement.text);
        setHeading(res.data.announcement?.heading);
        setContentType({
          label: res.data.announcement.announcement_type,
          value: res.data.announcement.announcement_type,
        });
        setDocumentType(res.data.announcement.announcement_type);
        setAnnouncementUrl(res.data.announcement.url);
        setMetaEdit(res.data.announcement);
      }
    } catch (e) {
      showNotification(apiMessage(e, "Could not load the announcement"), "error");
    }
  }
  useEffect(() => {
    if (id) {
      fetchannouncement(id);
      fetchVersionList(id);
    }
  }, [id]);

  const showError = (keyName) => {
    return errors && errors[keyName] ? (
      <p className="text-red-500 text-[14px]  pt-2">{errors[keyName]}</p>
    ) : null;
  };

const handleMetaChange = (keyName, keyValue) => {
  setMetaEdit((prev) => ({
    ...prev,
    [`updated_${keyName}`]: keyValue,
    is_meta_edited: true, 
  }));
  setErrors((prev) => ({
    ...prev,
    [`updated_${keyName}`]: "",
  }));
};


  const handleValidate = (data: any) => {
    let isValid = true;
    let error: { [index: string]: any } = {};
    if (!data?.url || data?.url === "") {
      isValid = false;
      error["url"] = "Please enter url";
    }
    // if (!data?.canonical_url || data?.canonical_url === "") {
    //   isValid = false;
    //   error["canonical_url"] = "Please enter canonical url";
    // }
    if (!data?.page_title || data?.page_title === "") {
      isValid = false;
      error["page_title"] = "Please enter page title";
    }
    if (!data?.meta_description || data?.meta_description === "") {
      isValid = false;
      error["meta_description"] = "Please enter meta description";
    }
    // if (!data?.meta_keyword || data?.meta_keyword === "") {
    //   isValid = false;
    //   error["meta_keyword"] = "Please enter meta keyword";
    // }
    return {
      isValid,
      error,
    };
  };

  const userHasPermissionToApprovePage = () => {
    return permission?.permissions?.includes("announcement:verify");
  };

  const userHasPermissionToPublishPage = () => {
    return permission?.permissions?.includes("announcement:publish");
  };

  // const hasEditMode = () => {
  //   return announcementData?.is_edit;
  // };

  const hasApproved = () => {
    return announcementData?.is_approved;
  };

  const handleMetaData = async () => {
    let check = handleValidate(metaEdit);
    if (check.isValid) {
      try {
        const res = await callApi(
          `v1/announcement/${id}/update-announcement`,
          "post",
          metaEdit
        );
        if (res && res?.status === "Success") {
          showNotification("Updated successfully", "success");
          toggleMeta();
          fetchVersionList(id);
        }
      } catch (e) {
        showNotification(apiMessage(e, "Error occured"), "error");
      }
    } else {
      setErrors(check.error);
    }
  };
  const fetchVersionList = async (id) => {
    try {
      const res = await callApi(
        `v1/announcement/${id}/version-list`,
        "get",
        {}
      );
      if (res && res?.status === "Success") {
        setVersion(res.data?.copies);
      }
    } catch (e) {
      showNotification(apiMessage(e, "Could not load the pending edit"), "error");
    }
  };

  // Check if any segment has is_edit = true
  const hasEditMode = () => version?.is_edit;

  const handleAnnouncementChange = (keyName, keyValue) => {
    if (keyValue.value === "select...") {
      setDocumentType("");
    } else {
      setDocumentType(keyValue.value);
    }
    setContentType(keyValue);
  };
  const handlePublish = async (id: any) => {
    // Publishing an edit that keeps the same file is allowed, but worth a second look.
    if (sameDocument(version?.thumbnail_url, announcementData?.thumbnail_url)) {
      const proceed = window.confirm(
        `The document file in this edit is the same as the live one (${documentLabel(
          version?.thumbnail_url
        )}). If you meant to replace the PDF, press Cancel and pick the new file. Publish anyway?`
      );
      if (!proceed) return;
    }
    try {
      const res = await callApi(`v1/announcement/${id}/publish`, "get", {});
      if (res && res?.status === "Success") {
        showNotification("Published successfully", "success");
        fetchannouncement(id);
        setShowApprove(false);
        fetchVersionList(id);
      }
    } catch (e) {
      showNotification(apiMessage(e, "Publishing failed"), "error");
    }
  };

  const handleApprove = async () => {
    if (!announcementData) {
      showNotification("Page data is missing. Please try again.", "error");
      return;
    }
    try {
      const response = await callApi(`v1/announcement/${id}/approve`, "get");
      if (response?.status === "Success") {
        setLoader(!loader);
        showNotification("Page approved successfully", "success");
        fetchannouncement(id);
        setShowApprove(true);
      }
    } catch (error) {
      showNotification(apiMessage(error, "Approving failed. Please try again."), "error");
    }
  };
  // validation
  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      heading: "hh",
    },
    validationSchema: Yup.object({
      heading: Yup.string().required("Please Enter heading"),
    }),

    onSubmit: async (values) => {
      const updateData = {
        ...values,
      };
      if (!heading) {
        setHeadingError("Please add heading");
        showNotification("Please add heading", "error");
        return;
      }
      if (!!!imageLink?.media_url && !!!imageLink?.thumbnail_url) {
        showNotification("Please add a document first (use Add Document or Change).", "error");
        return;
      }

      if (!documentType) {
        showNotification("Please add announcement type", "error");
        return;
      }
      if (announcementUrl) {
        try {
          setIsLoading(true);
          const res = await callApi(
            `v1/announcement/${id}/update-announcement`,
            "post",
            {
              heading: heading,
              thumbnail_url: imageLink?.media_url,
              thumbnail_media_id: imageLink?._id,
              announcement_type: documentType,
              url: announcementUrl,
              ...formData,
            }
          );
          setIsLoading(false);
          if (res && res?.status === "Success") {
            const savedPath = res?.data?.result?.updatedPage?.thumbnail_url || imageLink?.thumbnail_url;
            const label = documentLabel(savedPath);
            if (sameDocument(savedPath, announcementData?.thumbnail_url)) {
              showNotification(
                `Saved, but the document is still the live one (${label}). Pick a new file with Change if you meant to replace it.`,
                "warning"
              );
            } else {
              showNotification(`Saved. Document in this edit: ${label}. It is not live until you Approve and Publish.`, "success");
            }
            setImageLink("");
            fetchVersionList(id);
            setIsLoading(true);
            // The message above disappears with this page, so give it time to be read.
            const unchanged = sameDocument(savedPath, announcementData?.thumbnail_url);
            setTimeout(() => {
              navigate(ANNOUNCEMENT_LIST_ROUTE);
              setIsLoading(false);
            }, unchanged ? 4500 : 3000);
          }
        } catch (e) {
          showNotification(apiMessage(e, "Could not save the announcement"), "error");
          setIsLoading(false);
        }
      }
    },
  });

  return (
    <>
      <div className="flex justify-between mt-2">
        <BreadCrumb
          title={ANNOUNCEMENT_UPDATE_ROUTE.replace(":id", id)}
          pageTitle={ANNOUNCEMENT_LIST_ROUTE}
          version="v1"
        />
        <div>
        {userHasPermissionToApprovePage() &&
            hasEditMode() &&!showApprove && (
            <div
              className="text-white cursor-pointer btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              onClick={handleApprove}
            >
              Approve
            </div>
          )}

          {userHasPermissionToPublishPage() && showApprove && hasApproved() && (
            <button
              type="button"
              className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              onClick={() => {
                handlePublish(id);
              }}
            >
              <span className="align-middle">Publish</span>
            </button>
          )}
          <button
            type="button"
            className="ml-2 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            onClick={() => toggleMeta()}
          >
            <span className="align-middle">Edit Meta</span>
          </button>
        </div>
      </div>
      <ToastContainer />
      {(() => {
        const pending = !!hasEditMode();
        const step = currentEditStep(pending, !!(hasApproved() || showApprove));
        const steps: { key: string; label: string }[] = [
          { key: "save", label: "1. Save changes" },
          { key: "approve", label: "2. Approve" },
          { key: "publish", label: "3. Publish" },
        ];
        return (
          <div
            id="announcement-edit-status"
            className="p-4 mb-4 text-sm border rounded-md border-slate-200 dark:border-zink-500"
          >
            <p className="font-medium">
              {pending
                ? "Unpublished edit. The website still shows the live version until you Approve and Publish."
                : "No pending edit. The website shows what you see here."}
            </p>
            {pending && (
              <p className="mt-1 text-slate-500 dark:text-zink-200">
                Live document: {documentLabel(announcementData?.thumbnail_url) || "none"}
                {" · "}
                Document in this edit: {documentLabel(version?.thumbnail_url) || "same as live"}
                {sameDocument(version?.thumbnail_url, announcementData?.thumbnail_url)
                  ? " (unchanged)"
                  : ""}
              </p>
            )}
            <ol className="flex flex-wrap gap-4 mt-2">
              {steps.map((item) => (
                <li
                  key={item.key}
                  className={
                    item.key === step
                      ? "font-semibold text-custom-500"
                      : "text-slate-400 dark:text-zink-300"
                  }
                >
                  {item.label}
                  {item.key === step ? " (next)" : ""}
                </li>
              ))}
            </ol>
          </div>
        );
      })()}
      <div className="grid grid-cols-12 gap-x-5">
        <div className="col-span-12 card 2xl:col-span-12">
          <div className="p-6">
            <form
              className="create-form"
              id="create-form"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <input type="hidden" value="" name="id" id="id" />
              <input type="hidden" value="add" name="action" id="action" />
              <input type="hidden" id="id-field" />
              <div
                id="alert-error-msg"
                className="hidden px-4 py-3 text-sm text-red-500 border border-transparent rounded-md bg-red-50 dark:bg-red-500/20"
              ></div>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                <div className="xl:col-span-12">
                  <CustomInput
                    label="Heading"
                    name="heading"
                    type="text"
                    value={heading}
                    placeholder="Enter heading"
                    onChange={(e) => setHeading(e.target.value)}
                    isVersioned={version?.heading ? true : false}
                    versionedValue={version?.heading}
                  />

                  {headingError ? (
                    <p className="text-red-400">{headingError}</p>
                  ) : null}
                  {validation.touched.heading && validation.errors.heading ? (
                    <p className="text-red-400">{validation.errors.heading}</p>
                  ) : null}
                </div>
                <div className="mt-4 xl:col-span-6">
                  <label
                    htmlFor="phoneNumberInput3"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Announcement Type
                  </label>
                  <ReactSelect
                    options={announcementType}
                    isSearchable={false}
                    isMulti={false}
                    placeholder={"Please select announcement type"}
                    contentType={contentType}
                    handleContentChange={handleAnnouncementChange}
                    name={"announcement_type"}
                    id={"content-type1"}
                    versionData={
                      version?.announcement_type
                        ? {
                            label: version?.announcement_type,
                            value: version?.announcement_type,
                          }
                        : null
                    }
                  />
                </div>
              </div>
              <div className="xl:col-span-12">
                <CustomDateInput
                  value={formData?.released_at || ""} // Default to empty string if undefined
                  dateOptions={{ dateFormat: "Y-m-d" }}
                  isRequired={true}
                  keyName="released_at"
                  label="Release Date"
                  setDate={(selectedDates) => {
                    if (selectedDates && selectedDates[0]) {
                      setFormData((prev) => ({
                        ...prev,
                        released_at: selectedDates[0].toISOString(),
                      }));
                    }
                  }}
                  isVersioned={!!version?.released_at} // Only true if version.released_at is defined
                  versionedValue={
                    version?.released_at
                      ? moment(new Date(version.released_at)).toISOString()
                      : ""
                  } // Default empty string if not defined
                  placeholder="Select Date"
                  errors={""}
                />
              </div>
              <div className="m-4 ml-0 xl:col-span-12">
                {imageLink ? (
                  <div className="flex items-start">
                    <div className="relative w-[175px] h-40 shrink-0">
                      <embed
                        src={getImageUrl(
                          imageLink?.media_url || imageLink?.thumbnail_url
                        )}
                        type="application/pdf"
                        width="165"
                        height="165"
                      />
                      <span
                        style={{
                          position: "absolute",
                          top: "0px",
                          right: "6px",
                          fontSize: "22px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setImageData(null);
                          setImageLink(null);
                        }}
                      >
                        ×
                      </span>
                    </div>

                    <div className="ml-4">
                      <p className="mb-1 text-sm font-medium break-all" id="selected-document-name">
                        {documentLabel(imageLink?.media_url || imageLink?.thumbnail_url)}
                      </p>
                      {documentUploadedAt(imageLink?.media_url || imageLink?.thumbnail_url) && (
                        <p className="mb-3 text-xs text-slate-500">
                          Uploaded {documentUploadedAt(imageLink?.media_url || imageLink?.thumbnail_url)}
                        </p>
                      )}
                      <div
                        onClick={() => {
                          setImageData(undefined);
                          toggleImage();
                        }}
                        className="text-white cursor-pointer btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                      >
                        Change
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      onClick={() => {
                        setImageData(undefined);
                        toggleImage();
                      }}
                      className="text-white cursor-pointer btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    >
                      {"Add Document"}
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="reset"
                  id="close-modal"
                  data-modal-close="addEmployeeModal"
                  className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                  onClick={() => navigate(ANNOUNCEMENT_LIST_ROUTE)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  id="addNew"
                  disabled={isLoading}
                  style={{
                    minWidth: "170px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                >
                  {isLoading ? (
                    <span>
                      <TailSpin
                        visible={true}
                        height="20"
                        width="20"
                        color="#fff"
                      />
                    </span>
                  ) : (
                    "Update announcement"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Drawer
        show={showMeta}
        onHide={toggleMeta}
        id="drawerEnd"
        drawer-end="true"
        className="fixed inset-y-0 flex flex-col w-1/2 transition-transform duration-300 ease-in-out transform bg-white shadow ltr:right-0 rtl:left-0 z-drawer dark:bg-zink-600"
      >
        <Drawer.Header
          className="flex items-center justify-between p-4 border-b card-body border-slate-200 dark:border-zink-500"
          closeButtonClass="lucide lucide-x size-4 transition-all duration-200 ease-linear text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"
        >
          <Drawer.Title className="text-15">{"Edit"}</Drawer.Title>
          <X className="transition-all duration-200 ease-linear size-4 text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"></X>
        </Drawer.Header>
        <Drawer.Body className="h-full p-4 overflow-y-auto">
          <MetaData
            modelName="announcement"
            handleMetaChange={handleMetaChange}
            metaEdit={metaEdit}
            showError={showError}
            toggleMeta={toggleMeta}
            handleMetaData={handleMetaData}
            loader={loader}
          />
        </Drawer.Body>
      </Drawer>
      <Drawer
        show={showImage}
        onHide={toggleImage}
        id="drawerEnd"
        drawer-end="true"
        className="fixed inset-y-0 flex flex-col w-1/2 transition-transform duration-300 ease-in-out transform bg-white shadow ltr:right-0 rtl:left-0 z-drawer dark:bg-zink-600"
      >
        <Drawer.Header
          className="flex items-center justify-between p-4 border-b card-body border-slate-200 dark:border-zink-500"
          closeButtonClass="lucide lucide-x size-4 transition-all duration-200 ease-linear text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"
        >
          <Drawer.Title className="text-15">{"Select Document"}</Drawer.Title>
          <X className="transition-all duration-200 ease-linear size-4 text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"></X>
        </Drawer.Header>
        <Drawer.Body className="h-full p-4 overflow-y-auto">
          <ImageView
            showImage={showImage}
            setEventData={setImageData}
            togleImage={toggleImage}
          />
        </Drawer.Body>
      </Drawer>
    </>
  );
};

export default UpdateAnnouncement;
